import {
    Component,
    computed,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import {
    DxDropDownButtonComponent,
    DxDropDownButtonTypes,
} from 'devextreme-angular/ui/drop-down-button';
import {
    EMBEDED_QUERY_PARAM,
    EmbededModeService,
    EmbededPage,
    HeaderContentService,
} from '../../shared/services';
import * as packageInfo from '../../../../package.json';

/**
 * The layout for embedded mode (see EmbededModeService): no side menu, no
 * user panel — a slim bar with a dropdown of the embeddable pages, the
 * current page's own header controls, and the version. The host frame is a
 * fixed size, so the bar stays pinned while the page scrolls under it.
 */
@Component({
    selector: 'app-embeded-toolbar',
    templateUrl: './embeded-toolbar.component.html',
    styleUrls: ['./embeded-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDropDownButtonComponent, NgTemplateOutlet],
})
export class EmbededToolbarComponent {
    private router = inject(Router);
    // Pages project their header controls here exactly as they would into
    // the full header — a page marked showInEmbeded keeps its menu.
    headerContent = inject(HeaderContentService);

    pages = inject(EmbededModeService).pages;
    packageInfo = packageInfo;

    private url = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map((e) => e.urlAfterRedirects),
        ),
        // The initial NavigationEnd can fire before this component exists.
        { initialValue: this.router.url },
    );

    // Undefined on a page outside the list — the bar then just asks for one.
    currentPage = computed(() => {
        const path = this.url().split(/[?#]/)[0].replace(/^\/+/, '');
        return this.pages.find((page) => page.path === path);
    });

    onPageClick(e: DxDropDownButtonTypes.ItemClickEvent) {
        const page = e.itemData as EmbededPage;
        // Keep the flag in the URL. The mode itself no longer needs it, but
        // a frame URL that says what it is stays reproducible when copied.
        this.router.navigate(['/' + page.path], {
            queryParams: { [EMBEDED_QUERY_PARAM]: 'true' },
        });
    }
}
